import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, RefreshCw, AlertTriangle, Mail, Database, Sparkles, FileWarning } from 'lucide-react';
import { authFetch } from '../lib/api';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Sydney-formatted timestamp
const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney', day: '2-digit', month: 'short',
      year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// =========================================================================
// Each sub-panel hits its OWN existing endpoint — no aggregator, no cache
// surprises. Each is a small, self-contained component.
// =========================================================================

function EmailErrorsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  // Manual flush — admin can re-fire every transient failure at once.
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Existing battle-tested endpoint, just filtered to failed sends
      const res = await authFetch(`${API}/sent-emails?status=failed&limit=100&_t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(`Failed to load email errors: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const retryAll = useCallback(async () => {
    setRetrying(true);
    try {
      const res = await authFetch(`${API}/sent-emails/retry-failed?only_transient=true`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(
        `Retry: ${data.succeeded} sent, ${data.failed} still failing, ${data.skipped} skipped`,
      );
      await load();
    } catch (e) {
      toast.error(`Retry failed: ${e.message}`);
    } finally {
      setRetrying(false);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Failed email sends. Total: <strong>{total}</strong>
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={retryAll}
            disabled={retrying || total === 0}
            data-testid="email-errors-retry-all"
            title="Re-send every failed email whose error looks transient (timeouts, 5xx, auth)"
          >
            {retrying ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
            )}
            Retry All Failed
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="email-errors-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">No failed emails. 🎉</p>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {rows.map((e) => (
            <div key={e.email_id} className="border rounded-md p-3 text-sm bg-red-50 dark:bg-red-950/20 border-red-200" data-testid={`email-error-${e.email_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{e.subject || '(no subject)'}</p>
                  <p className="text-xs text-muted-foreground truncate">To: {e.to_email}</p>
                  {e.gmail_error && (
                    <p className="text-xs text-red-700 mt-1 break-words">{e.gmail_error}</p>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">
                  {fmt(e.sent_at)}
                  <p>{e.user_name}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BackupErrorsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  // Per-tick scheduled-cron audit (Mar 4, 2026 — added so misses show up
  // even when no exception was raised inside create_database_backup).
  const [runs, setRuns] = useState([]);
  // Loud failure banner — populated when a scheduled run fully failed
  // OR the main backup is missing.
  const [alerts, setAlerts] = useState([]);
  const [acking, setAcking] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Original endpoint — exception traces from manual + scheduled runs
      const res = await authFetch(`${API}/reports/backup-error-logs?limit=50&_t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(data.logs || []);

      // New endpoints — scheduled-run audit + loud-alert surface.
      // Fetched in parallel; failures don't block the original logs view.
      const [runsRes, alertsRes] = await Promise.all([
        authFetch(`${API}/reports/backup-runs?limit=30&_t=${Date.now()}`, { cache: 'no-store' }),
        authFetch(`${API}/reports/backup-alerts?only_unacknowledged=true&_t=${Date.now()}`, { cache: 'no-store' }),
      ]);
      if (runsRes.ok) {
        const rd = await runsRes.json();
        setRuns(rd.runs || []);
      }
      if (alertsRes.ok) {
        const ad = await alertsRes.json();
        setAlerts(ad.alerts || []);
      }
    } catch (e) {
      toast.error(`Failed to load backup errors: ${e.message}`);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const acknowledgeAlert = async (runId) => {
    setAcking((p) => ({ ...p, [runId]: true }));
    try {
      const res = await authFetch(`${API}/reports/backup-alerts/${runId}/acknowledge`, {
        method: 'POST',
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        setAlerts((a) => a.filter((x) => x.run_id !== runId));
      } else {
        toast.error(`Failed to acknowledge (${res.status})`);
      }
    } catch (e) {
      toast.error('Failed to acknowledge');
    } finally {
      setAcking((p) => ({ ...p, [runId]: false }));
    }
  };

  const triggerBackupNow = async () => {
    try {
      const res = await authFetch(`${API}/reports/scheduled-backup-now`, { method: 'POST' });
      if (res.ok) {
        toast.success('Backup triggered. Refresh in ~30s to see the result.');
      } else {
        toast.error(`Failed to trigger backup (${res.status})`);
      }
    } catch (e) {
      toast.error('Failed to trigger backup');
    }
  };

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      {/* LOUD failure banner — appears whenever a scheduled run fully
          failed OR the main DB backup is missing for the most recent
          tick. Operator must acknowledge to clear it. */}
      {alerts.length > 0 && (
        <div
          className="border-l-4 border-red-600 bg-red-50 dark:bg-red-950/30 p-3 rounded-md"
          data-testid="backup-alert-banner"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-red-900 dark:text-red-100">
                  Scheduled backup failed — needs attention
                </p>
                <p className="text-xs text-red-800 dark:text-red-200 mt-0.5">
                  {alerts.length} unacknowledged alert{alerts.length === 1 ? '' : 's'} since this banner was last cleared.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-2 space-y-1.5">
            {alerts.map((a) => (
              <div
                key={a.run_id}
                className="text-xs bg-white dark:bg-red-950/50 border border-red-200 rounded px-2 py-1.5 flex items-start justify-between gap-2"
                data-testid={`backup-alert-${a.run_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{fmt(a.alert_at)} · {a.status}</p>
                  <p className="text-red-800 dark:text-red-200 break-words mt-0.5">
                    {a.summary || (a.errors || []).join(' · ') || 'No detail'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs shrink-0"
                  onClick={() => acknowledgeAlert(a.run_id)}
                  disabled={!!acking[a.run_id]}
                  data-testid={`acknowledge-alert-${a.run_id}`}
                >
                  Acknowledge
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">
          Recent backup runs (success and failure). Logs: <strong>{logs.length}</strong> · Scheduled ticks: <strong>{runs.length}</strong>
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={triggerBackupNow}
            data-testid="trigger-backup-now"
          >
            <Database className="w-3.5 h-3.5 mr-1" /> Backup now
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="backup-errors-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Scheduled-tick audit — proves the cron IS running (or shows
          gaps if it isn't). Each tick exposes status + the audit row
          even when no exception traceback exists. */}
      {runs.length > 0 && (
        <details className="border rounded-md" data-testid="backup-runs-history">
          <summary className="cursor-pointer p-2 text-sm bg-muted/30 hover:bg-muted/50 font-medium">
            Scheduled cron history ({runs.length} ticks)
          </summary>
          <div className="max-h-[280px] overflow-y-auto divide-y">
            {runs.map((r) => {
              const isFail = r.status === 'failed';
              const isPartial = r.status === 'partial';
              return (
                <div
                  key={r.run_id}
                  className={`p-2 text-xs ${
                    isFail ? 'bg-red-50 dark:bg-red-950/20'
                      : isPartial ? 'bg-amber-50 dark:bg-amber-950/20'
                        : 'bg-emerald-50 dark:bg-emerald-950/20'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Badge className={
                      isFail ? 'bg-red-200 text-red-900'
                        : isPartial ? 'bg-amber-200 text-amber-900'
                          : 'bg-emerald-200 text-emerald-900'
                    }>
                      {r.status?.toUpperCase()}
                    </Badge>
                    <span className="text-muted-foreground whitespace-nowrap">{fmt(r.started_at)}</span>
                  </div>
                  <div className="text-muted-foreground mt-1">
                    main: {r.main_filename || '✕'} {r.main_drive_uploaded && '↑'}
                    {' · '}
                    emails: {r.emails_filename || '✕'} {r.emails_drive_uploaded && '↑'}
                  </div>
                  {(r.errors || []).length > 0 && (
                    <pre className="text-[10px] bg-background border rounded p-1 mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words">
                      {(r.errors || []).join('\n')}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </details>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : logs.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">No backup logs yet. 🎉</p>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {logs.map((log, idx) => {
            const failed = log.success === false || !!log.error;
            return (
              <div
                key={idx}
                className={`border rounded-md p-3 text-sm ${failed ? 'bg-red-50 dark:bg-red-950/20 border-red-200' : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200'}`}
                data-testid={`backup-log-${idx}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <Badge className={failed ? 'bg-red-200 text-red-900' : 'bg-emerald-200 text-emerald-900'}>
                    {failed ? 'FAILED' : 'OK'}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">{fmt(log.timestamp)}</span>
                </div>
                <p className="text-xs font-medium">{log.operation || 'backup'}{log.user_email ? ` — ${log.user_email}` : ''}</p>
                {log.error && <p className="text-xs text-red-700 mt-1 break-words">{log.error}</p>}
                {log.steps && log.steps.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                      {log.steps.length} step{log.steps.length !== 1 ? 's' : ''}
                    </summary>
                    <pre className="bg-muted p-2 rounded text-[10px] mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words">
                      {JSON.stringify(log.steps, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SophieErrorsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Existing endpoint used by the old standalone Sophie Errors panel
      const res = await authFetch(`${API}/ai/assistant/errors?_t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.errors || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(`Failed to load Sophie errors: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Errors encountered by Sophie during conversations. Total: <strong>{total}</strong>
        </p>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sophie-errors-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">No Sophie errors. 🎉</p>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {rows.map((err, idx) => (
            <div
              key={err.error_id || idx}
              className="border rounded-md p-3 text-sm bg-red-50 dark:bg-red-950/20 border-red-200"
              data-testid={`sophie-error-${err.error_id || idx}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px]">{err.error_type || 'Error'}</Badge>
                    <span className="text-xs text-muted-foreground">{err.provider}/{err.model}</span>
                  </div>
                  <p className="font-medium text-red-800 break-words">{err.error_message}</p>
                  {err.user_message && (
                    <p className="text-xs text-muted-foreground mt-1">User asked: "{err.user_message}"</p>
                  )}
                </div>
                <div className="text-right text-[11px] text-muted-foreground shrink-0">
                  {fmt(err.timestamp)}
                  <p>{err.user_email}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImportErrorsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Existing endpoint used by the old standalone Notes Import panel
      const res = await authFetch(`${API}/import-data/notes/errors?limit=100&_t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(`Failed to load import errors: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const catColor = (c) => ({
    duplicate: 'bg-amber-100 text-amber-800',
    skipped: 'bg-slate-100 text-slate-800',
    error: 'bg-red-100 text-red-800',
    fatal: 'bg-red-200 text-red-900',
  }[c] || 'bg-gray-100 text-gray-800');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Skipped / duplicate / error rows from Notes Imports. Total: <strong>{total}</strong>
        </p>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="import-errors-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">No import errors. 🎉</p>
      ) : (
        <div className="overflow-x-auto max-h-[500px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b">
                <th className="text-left p-2 font-medium">When</th>
                <th className="text-left p-2 font-medium">Category</th>
                <th className="text-left p-2 font-medium">Organisation</th>
                <th className="text-left p-2 font-medium">Date</th>
                <th className="text-left p-2 font-medium">Reason</th>
                <th className="text-left p-2 font-medium">File</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.log_id} className="border-b hover:bg-muted/40">
                  <td className="p-2 whitespace-nowrap text-muted-foreground">{fmt(r.created_at)}</td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 rounded ${catColor(r.category)}`}>{r.category}</span>
                  </td>
                  <td className="p-2 max-w-xs truncate" title={r.organization}>{r.organization}</td>
                  <td className="p-2 whitespace-nowrap">{r.date}</td>
                  <td className="p-2 max-w-md" title={r.reason}>{r.reason}</td>
                  <td className="p-2 max-w-[160px] truncate" title={r.filename}>{r.filename}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Main panel — pure tab wrapper. NO aggregator endpoint, NO "All" view.
// Each tab is self-contained and hits its own existing API endpoint.
// =========================================================================

const TABS = [
  { id: 'email',  label: 'Email',        icon: Mail,           Component: EmailErrorsTab },
  { id: 'backup', label: 'Backup',       icon: Database,       Component: BackupErrorsTab },
  { id: 'sophie', label: 'Sophie AI',    icon: Sparkles,       Component: SophieErrorsTab },
  { id: 'import', label: 'Notes Import', icon: FileWarning,    Component: ImportErrorsTab },
];

export default function ErrorLogsPanel() {
  const [active, setActive] = useState('email');

  return (
    <Card data-testid="error-logs-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Error Logs
        </CardTitle>
        <CardDescription>
          Failed operations across the system, grouped by source. Each tab loads independently.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="grid grid-cols-4 w-full">
            {TABS.map(t => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`error-logs-tab-${t.id}`} className="text-xs">
                <t.icon className="w-3 h-3 mr-1" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map(t => (
            <TabsContent key={t.id} value={t.id} className="mt-4">
              <t.Component />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
